import { useState, Fragment } from 'react';
import { api } from '../lib/api';

/**
 * WxCC Clean Up Tools — dependency-ordered, DRY-RUN-FIRST bulk cleanup of Webex
 * Contact Center config objects. Two modes: Target-only (include — delete ONLY what
 * you name; safest for prod) and Protect (delete all except protected). Per-row
 * select, key search, Show Dependencies, and type-to-confirm before any delete.
 * Object list follows the Control-Hub cleanup nav order. Demo data until a worker URL is set.
 */

// display order = the cleanup-tool nav order (snapshot)
const TYPES: { key: string; label: string; api: boolean }[] = [
  { key: 'users', label: 'Users', api: true },
  { key: 'entry_points', label: 'Entry Points', api: true },
  { key: 'flows', label: 'Flows', api: false },
  { key: 'business_hours', label: 'Business Hours', api: false },
  { key: 'overrides', label: 'Overrides', api: false },
  { key: 'holidays', label: 'Holidays', api: false },
  { key: 'user_profiles', label: 'User Profiles', api: true },
  { key: 'resource_collections', label: 'Resource Col.', api: true },
  { key: 'desktop_layouts', label: 'Desktop Layouts', api: true },
  { key: 'queues', label: 'Queues', api: true },
  { key: 'teams', label: 'Teams', api: true },
  { key: 'sites', label: 'Sites', api: true },
  { key: 'multimedia_profiles', label: 'Multimedia Prof', api: true },
  { key: 'desktop_profiles', label: 'Desktop Prof', api: true },
  { key: 'skill_profiles', label: 'Skill Prof', api: true },
  { key: 'skill_definitions', label: 'Skill Def', api: true },
  { key: 'work_types', label: 'Work Types', api: true },
  { key: 'bots_ai_agents', label: 'Bots & AI Agents', api: false },
  { key: 'knowledge_bases', label: 'Knowledge Bases', api: false },
  { key: 'visualizations', label: 'Visualizations', api: false },
];
const DEP_MAP: Record<string, { blocked_by: string[]; orphans: string[] }> = {
  entry_points: { blocked_by: [], orphans: ['flows', 'queues'] },
  queues: { blocked_by: ['flows', 'entry_points'], orphans: ['teams', 'skill_profiles'] },
  teams: { blocked_by: ['queues', 'users'], orphans: ['sites', 'multimedia_profiles', 'desktop_layouts'] },
  users: { blocked_by: [], orphans: ['teams', 'user_profiles', 'desktop_profiles', 'skill_profiles'] },
  skill_profiles: { blocked_by: ['users', 'queues'], orphans: ['skill_definitions'] },
  skill_definitions: { blocked_by: ['skill_profiles'], orphans: [] },
  work_types: { blocked_by: ['user_profiles'], orphans: [] },
  user_profiles: { blocked_by: ['users'], orphans: ['work_types'] },
  desktop_profiles: { blocked_by: ['users'], orphans: [] },
  multimedia_profiles: { blocked_by: ['teams', 'users'], orphans: [] },
  desktop_layouts: { blocked_by: ['teams'], orphans: [] },
  sites: { blocked_by: ['teams'], orphans: [] },
  resource_collections: { blocked_by: [], orphans: [] },
};

type Row = { type: string; id: string; name: string; action: string; reason?: string };
type Plan = { dry_run: boolean; mode: string; org_id: string; order: string[]; counts: Record<string, number>; plan: Row[] };

const DEMO: Plan = {
  dry_run: true, mode: 'include', org_id: 'demo-org', order: ['queues', 'teams'],
  counts: { delete: 2, excluded: 0, system: 1, not_targeted: 2, refused_no_api: 0 },
  plan: [
    { type: 'queues', id: 'q1', name: 'Queue-1', action: 'DELETE' },
    { type: 'queues', id: 'q2', name: 'Police_Main_Q', action: 'SKIP_NOT_TARGETED', reason: 'not in target list' },
    { type: 'queues', id: 'q3', name: 'WxCC_Test', action: 'DELETE' },
    { type: 'queues', id: 'q4', name: 'Default_Queue', action: 'SKIP_SYSTEM', reason: 'system/default' },
    { type: 'teams', id: 't1', name: 'Team-1', action: 'SKIP_NOT_TARGETED', reason: 'not in target list' },
  ],
};
const ACT: Record<string, string> = {
  DELETE: 'text-rose-600 bg-rose-50', SKIP_EXCLUDED: 'text-emerald-700 bg-emerald-50',
  SKIP_NOT_TARGETED: 'text-gray-500 bg-gray-50', SKIP_SYSTEM: 'text-gray-500 bg-gray-100',
  REFUSED_NO_API: 'text-amber-700 bg-amber-50', LIST_ERROR: 'text-amber-700 bg-amber-50',
};
const labelOf = (k: string) => TYPES.find((t) => t.key === k)?.label || k;

export default function WxccCleanupPanel() {
  const [base, setBase] = useState(() => localStorage.getItem('wxccCleanupApi') || '');
  const [tenant, setTenant] = useState('longbeach');
  const [mode, setMode] = useState<'include' | 'protect'>('include');
  const [sel, setSel] = useState<Set<string>>(new Set(['queues']));
  const [filterVal, setFilterVal] = useState('');
  const [plan, setPlan] = useState<Plan | null>(null);
  const [live, setLive] = useState(false);
  const [search, setSearch] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [openDeps, setOpenDeps] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [runLog, setRunLog] = useState<string | null>(null);

  const setUrl = (v: string) => { setBase(v); localStorage.setItem('wxccCleanupApi', v.replace(/\/+$/, '')); };
  const toggleType = (k: string) => { const n = new Set(sel); n.has(k) ? n.delete(k) : n.add(k); setSel(n); };
  const rowKey = (r: Row) => `${r.type}:${r.id}`;

  const preview = async () => {
    setErr(null); setRunLog(null); setConfirmOpen(false);
    if (!sel.size) { setErr('Pick at least one object type on the left first.'); return; }
    setBusy(true);
    const b = base.replace(/\/+$/, '');
    const flt = filterVal ? [{ field: 'name', op: 'contains', value: filterVal }] : [];
    const body: any = { tenant, types: [...sel], mode };
    if (mode === 'include') body.include = flt; else body.filters = flt;
    try {
      let p: Plan;
      if (b) { const r = await fetch(b + '/cleanup/plan', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }); if (!r.ok) throw new Error('HTTP ' + r.status); p = await r.json(); } // dev: direct worker URL
      else { p = await api.wxccCleanupPlan(body); }                                                                                                       // prod: Shield-gated, session-auth
      if (!p || !Array.isArray(p.plan)) throw new Error('unexpected response: ' + JSON.stringify(p).slice(0, 180));
      setLive(true); setPlan(p);
      setChecked(new Set(p.plan.filter((x) => x.action === 'DELETE').map(rowKey))); // seed delete set from plan
    } catch (e: any) {
      setPlan(null);
      setErr(`Preview failed: ${e?.message || e}${e?.status ? ` (HTTP ${e.status})` : ''} · tenant "${tenant}". (Dry‑run is read‑only — nothing was changed.)`);
    }
    setBusy(false);
  };

  const toggleCheck = (r: Row) => { const k = rowKey(r); const n = new Set(checked); n.has(k) ? n.delete(k) : n.add(k); setChecked(n); };
  const toggleDeps = (r: Row) => { const k = rowKey(r); const n = new Set(openDeps); n.has(k) ? n.delete(k) : n.add(k); setOpenDeps(n); };

  const execute = async () => {
    if (confirmText !== 'DELETE') return;
    const rows = (plan?.plan || []).filter((r) => checked.has(rowKey(r))).map((r) => ({ ...r, action: 'DELETE' }));
    if (!live) { setRunLog(`(demo) would delete ${rows.length} objects on ${tenant} — nothing deleted in demo mode.`); setConfirmOpen(false); setConfirmText(''); return; }
    setBusy(true);
    try {
      // ALWAYS through the Shield-gated, super-admin-only backend (secret stays server-side)
      const d = await api.wxccCleanupExecute({ tenant, plan: rows });
      setRunLog(d.error ? `error: ${d.error}` : `Deleted ${d.deleted}, failed ${d.failed}, passes used ${d.passes_used}.`);
    } catch (e) { setRunLog('execute failed (needs super_admin session): ' + e); }
    setBusy(false); setConfirmOpen(false); setConfirmText('');
  };

  const c = plan?.counts || {};
  const deleteCount = checked.size;
  const feed = (plan?.plan || []).filter((r) => !search || (r.name + r.id + r.type).toLowerCase().includes(search.toLowerCase()));
  const Pill = ({ a }: { a: string }) => <span className={`px-2 rounded-full text-[10px] font-bold ${ACT[a] || ''}`}>{a.replace(/_/g, ' ')}</span>;

  return (
    <div className="space-y-3">
      <div className="card p-3 text-xs text-amber-800 bg-amber-50 border border-amber-200">
        <b>Destructive — gated.</b> Dry-run preview → tick exactly what to delete → type‑to‑confirm. <b>Target‑only</b> mode deletes ONLY what you name (safe for production); <b>Protect</b> mode deletes everything except what you protect.
      </div>

      <div className="flex gap-3 items-start">
        {/* LEFT: object types vertical sub-menu (cleanup nav order) */}
        <nav className="w-44 shrink-0 card p-2 max-h-[72vh] overflow-y-auto">
          <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1 px-1">Object types</div>
          <div className="flex flex-col gap-0.5">
            {TYPES.map((t) => t.api ? (
              <button key={t.key} onClick={() => toggleType(t.key)} className={`flex items-center gap-1.5 text-left text-[13px] px-2 py-1.5 rounded border-l-2 transition ${sel.has(t.key) ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 text-blue-700 dark:text-blue-300 font-medium' : 'border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
                <span className="w-3 shrink-0 text-blue-600 dark:text-blue-300">{sel.has(t.key) ? '✓' : ''}</span>{t.label}
              </button>
            ) : (
              <div key={t.key} className="flex items-center gap-1.5 text-[13px] px-2 py-1.5 text-amber-600/70 border-l-2 border-transparent" title="Phase 2 — no public API"><span className="w-3 shrink-0" />{t.label}<span className="text-[9px] ml-auto opacity-70">phase 2</span></div>
            ))}
          </div>
        </nav>

        {/* RIGHT: controls + results */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* controls */}
          <div className="card p-3 space-y-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${live ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>{live ? 'LIVE' : 'DEMO'}</span>
              <input className="flex-1 border rounded px-2 py-1 text-xs font-mono" placeholder="Dev worker URL (blank = Shield-gated backend)" value={base} onChange={(e) => setUrl(e.target.value)} />
              <input className="border rounded px-2 py-1 text-xs font-mono w-32" placeholder="tenant" value={tenant} onChange={(e) => setTenant(e.target.value)} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wide text-gray-500">Mode</span>
              <button onClick={() => setMode('include')} className={`text-xs px-2 py-0.5 rounded border ${mode === 'include' ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-semibold' : 'border-gray-200 text-gray-500'}`}>Target‑only (include)</button>
              <button onClick={() => setMode('protect')} className={`text-xs px-2 py-0.5 rounded border ${mode === 'protect' ? 'bg-rose-50 border-rose-300 text-rose-700 font-semibold' : 'border-gray-200 text-gray-500'}`}>Protect (delete all except)</button>
            </div>
        <div className="flex items-center gap-2">
          <label className="text-gray-500 text-xs whitespace-nowrap">{mode === 'include' ? 'Target where name contains' : 'Protect where name contains'}</label>
          <input className="flex-1 border rounded px-2 py-1 text-xs font-mono" placeholder={mode === 'include' ? 'e.g. Test, Queue-1, _Backup' : 'e.g. Police, Housing'} value={filterVal} onChange={(e) => setFilterVal(e.target.value)} />
          <button onClick={preview} disabled={busy || !sel.size} className="border rounded px-3 py-1 text-sm bg-gray-50">{busy ? '…' : '🔍 Preview (dry-run)'}</button>
        </div>
        {err && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">{err}</div>}
            {plan && deleteCount === 0 && (
              <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                Preview ran: <b>{(plan.plan || []).length}</b> objects scanned, <b>0</b> matched your {mode === 'include' ? 'target' : 'protect'} filter. {mode === 'include' ? 'Refine the “Target where name contains” text, or tick rows below to select them manually.' : 'Everything is currently protected.'}
              </div>
            )}
      </div>

      {plan && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[['Selected to delete', deleteCount, 'text-rose-600'], ['Protected', mode === 'include' ? (c.not_targeted || 0) : (c.excluded || 0), 'text-emerald-600'],
              ['System (skip)', c.system || 0, 'text-gray-500'], ['No API (skip)', c.refused_no_api || 0, 'text-amber-600'], ['Org', plan.org_id, 'text-gray-400 text-xs']].map(([l, n, cls]) => (
              <div key={l as string} className="card p-3"><div className={`text-2xl font-extrabold ${cls as string}`}>{n as any}</div><div className="text-[11px] uppercase tracking-wide text-gray-500">{l as string}</div></div>
            ))}
          </div>

          <div className="card p-3 text-xs text-gray-600">Deletion order (dependency-safe, multi-pass): <span className="font-mono">{plan.order.map(labelOf).join(' → ')}</span></div>

          {/* objects table with search */}
          <div className="card p-0 overflow-hidden">
            <div className="p-2 flex items-center justify-between border-b">
              <div className="text-xs uppercase tracking-wide text-gray-500">Objects — {feed.length} shown · tick exactly what to delete</div>
              <input className="border rounded px-2 py-1 text-xs w-56" placeholder="🔍 Search objects…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <table className="w-full text-xs">
              <thead><tr className="text-gray-400 text-left"><th className="px-3 py-1 w-8"></th><th>Type</th><th>Object</th><th>ID</th><th>Plan</th><th>Dependencies</th></tr></thead>
              <tbody>
                {feed.map((r) => {
                  const k = rowKey(r); const isDel = checked.has(k);
                  const selectable = r.action !== 'SKIP_SYSTEM' && r.action !== 'REFUSED_NO_API' && r.action !== 'LIST_ERROR';
                  const dep = DEP_MAP[r.type] || { blocked_by: [], orphans: [] };
                  return (
                    <Fragment key={k}>
                      <tr className={`border-t border-gray-50 ${isDel ? 'bg-rose-50/40' : ''}`}>
                        <td className="px-3 py-1">{selectable && <input type="checkbox" checked={isDel} onChange={() => toggleCheck(r)} />}</td>
                        <td className="text-gray-500 font-mono">{labelOf(r.type)}</td>
                        <td className="font-medium">{r.name}</td>
                        <td className="font-mono text-gray-400">{r.id?.slice(0, 8)}</td>
                        <td>{isDel ? <Pill a="DELETE" /> : <Pill a={r.action} />}</td>
                        <td><button onClick={() => toggleDeps(r)} className="text-[11px] px-2 py-0.5 rounded border border-indigo-300 text-indigo-600">Show Dependencies</button></td>
                      </tr>
                      {openDeps.has(k) && (
                        <tr className="bg-indigo-50/40"><td></td><td colSpan={5} className="px-3 py-2 text-[11px] text-gray-600">
                          <b>Blocked by</b> (must be deleted first): {dep.blocked_by.length ? dep.blocked_by.map(labelOf).join(', ') : 'none — top of the tree'} · <b>Will orphan</b>: {dep.orphans.length ? dep.orphans.map(labelOf).join(', ') : 'none'}.
                          <span className="text-gray-400"> The engine clears these in dependency order over multiple passes; if a delete returns a dependency error it's retried after its referencers go.</span>
                        </td></tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* confirm + execute */}
          <div className="card p-3 flex flex-wrap items-center gap-3">
            <div className="text-sm">Ready to delete <b className="text-rose-600">{deleteCount}</b> object{deleteCount === 1 ? '' : 's'} on <span className="font-mono">{tenant}</span></div>
            <button onClick={() => { setConfirmOpen(true); setConfirmText(''); }} disabled={!deleteCount} className={`ml-auto px-3 py-1 rounded text-sm font-semibold ${deleteCount ? 'bg-rose-600 text-white' : 'bg-gray-200 text-gray-400'}`}>Delete selected…</button>
          </div>

          {confirmOpen && (
            <div className="card p-4 border-2 border-rose-300 bg-rose-50/40 space-y-2">
              <div className="text-sm text-rose-700 font-semibold">Confirm deletion of {deleteCount} object{deleteCount === 1 ? '' : 's'} on “{tenant}”.</div>
              <div className="text-xs text-gray-600">This is irreversible. Type <b>DELETE</b> to confirm.</div>
              <div className="flex items-center gap-2">
                <input className="border rounded px-2 py-1 text-sm font-mono w-40" placeholder="type DELETE" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
                <button onClick={execute} disabled={confirmText !== 'DELETE' || busy} className={`px-3 py-1 rounded text-sm font-semibold ${confirmText === 'DELETE' ? 'bg-rose-600 text-white' : 'bg-gray-200 text-gray-400'}`}>Confirm delete</button>
                <button onClick={() => setConfirmOpen(false)} className="px-3 py-1 rounded text-sm border">Cancel</button>
              </div>
            </div>
          )}
          {runLog && <div className="card p-3 text-xs text-gray-700">{runLog}</div>}
        </>
      )}
      <div className="text-[11px] text-gray-500">Target‑only / Protect · per‑row select · key search · Show Dependencies · type‑to‑confirm · <b>Shield‑gated execute</b> (super_admin only; worker secret stays server‑side, logged to Shield) · dependency‑ordered multi‑pass.</div>
        </div>
      </div>
    </div>
  );
}
